import { useRef } from "react";
import { adminApi } from "@/lib/api";
import { Upload } from "lucide-react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const toArray = (s) => {
  if (Array.isArray(s)) return s;
  return (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
};

const fromArray = (a) => (Array.isArray(a) ? a.join(", ") : a || "");

export function PhotosManager({ photos, coverPhoto, onChange, uploading, setUploading }) {
  const fileInputRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newPhotos = [...(photos || [])];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await adminApi().post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPhotos.push(res.data.url);
      }
      onChange({
        photos: newPhotos,
        cover_photo: coverPhoto || newPhotos[0],
      });
    } catch {
      alert("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    const next = [...photos];
    const removed = photos[idx];
    next.splice(idx, 1);
    onChange({
      photos: next,
      cover_photo: coverPhoto === removed ? next[0] || "" : coverPhoto,
    });
  };

  const setCover = (url) => onChange({ cover_photo: url });

  return (
    <div className="mb-8">
      <div className="label-luxury mb-3">Photos · Upload from device</div>
      <div
        onClick={() => fileInputRef.current?.click()}
        className="border border-dashed border-white/20 hover:border-[var(--gold)]/60 p-10 text-center cursor-pointer transition-colors"
        data-testid="upload-dropzone"
      >
        <Upload size={28} className="text-[var(--gold)] mx-auto mb-3" />
        <div className="label-luxury">
          {uploading ? "Uploading..." : "Click to upload photos"}
        </div>
        <div className="text-white/40 text-xs mt-2 normal-case tracking-normal">
          JPG, PNG, WEBP — multiple allowed
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => uploadFiles(e.target.files)}
        className="hidden"
        data-testid="upload-input"
      />

      {photos && photos.length > 0 && (
        <div className="mt-5 grid grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((p, i) => (
            <div
              key={p}
              className={`relative aspect-[3/4] overflow-hidden border ${
                coverPhoto === p ? "border-[var(--gold)]" : "border-white/10"
              }`}
            >
              <img src={resolveUrl(p)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCover(p)}
                  data-testid={`set-cover-${i}`}
                  className="text-[0.6rem] uppercase tracking-[0.2em] text-[var(--gold)] border border-[var(--gold)]/50 px-2 py-1"
                >
                  {coverPhoto === p ? "Cover ✓" : "Set Cover"}
                </button>
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  data-testid={`remove-photo-${i}`}
                  className="text-[0.6rem] uppercase tracking-[0.2em] text-red-300 border border-red-300/40 px-2 py-1"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <details className="mt-5">
        <summary className="text-white/40 text-xs tracking-wider cursor-pointer hover:text-white/70">
          Or paste photo URLs (advanced)
        </summary>
        <div className="mt-3 grid md:grid-cols-2 gap-x-8 gap-y-5">
          <div>
            <div className="label-luxury mb-2">Cover Photo URL</div>
            <input
              data-testid="admin-field-cover_photo"
              className="luxury-input"
              value={coverPhoto || ""}
              onChange={(e) => onChange({ cover_photo: e.target.value })}
            />
          </div>
          <div>
            <div className="label-luxury mb-2">Photo URLs (comma)</div>
            <input
              className="luxury-input"
              value={fromArray(photos)}
              onChange={(e) => onChange({ photos: toArray(e.target.value) })}
            />
          </div>
        </div>
      </details>
    </div>
  );
}
